import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Gem, ShieldCheck, KeyRound, Loader2, ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import SEO from "@/components/SEO";

// -----------------------------------------------------------------------------
// Postmoro — 2FA challenge screen
// -----------------------------------------------------------------------------
// Rendered when /auth/me responds with { mfa_required: true }. The session
// cookie already exists but is in the ``mfa_pending`` state, so no other
// protected route is reachable until POST /api/auth/2fa/verify succeeds.
// -----------------------------------------------------------------------------

export default function Login2FA() {
  const navigate = useNavigate();
  const { user, refresh, logout } = useAuth();
  const [mode, setMode] = useState("code"); // "code" | "recovery"
  const [code, setCode] = useState("");
  const [recoveryCode, setRecoveryCode] = useState("");
  const [loading, setLoading] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");

  const email = useMemo(() => user?.email || "", [user]);

  useEffect(() => {
    // If the user is fully authenticated already, route them out.
    if (user && user.mfa_required !== true && user.email) {
      navigate("/dashboard", { replace: true });
    }
  }, [user, navigate]);

  const handleSubmit = async (e) => {
    e?.preventDefault?.();
    setErrorMsg("");
    if (mode === "code" && !code.trim()) {
      setErrorMsg("Please enter the 6-digit code from your authenticator app.");
      return;
    }
    if (mode === "recovery" && !recoveryCode.trim()) {
      setErrorMsg("Please enter one of your saved recovery codes.");
      return;
    }
    setLoading(true);
    try {
      const body = mode === "code"
        ? { code: code.trim() }
        : { recovery_code: recoveryCode.trim() };
      await api.post("/auth/2fa/verify", body);
      toast.success("Verified. Welcome back!");
      await refresh();
      navigate("/dashboard", { replace: true });
    } catch (err) {
      setErrorMsg(formatApiError(err, "Invalid or expired verification code."));
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = async () => {
    await logout();
    navigate("/login", { replace: true });
  };

  return (
    <div
      className="min-h-screen bg-[color:var(--bg-0)] text-[color:var(--text-1)] relative overflow-hidden"
      data-testid="login-2fa-page"
    >
      <SEO title="Two-factor authentication | Postmoro" path="/login/2fa" noindex />
      <div className="absolute inset-0 hero-glow pointer-events-none" />
      <div className="absolute -top-32 -left-32 w-[480px] h-[480px] rounded-full bg-[color:var(--brand-primary)] opacity-[0.08] blur-3xl pointer-events-none" />

      <header className="relative max-w-[1280px] mx-auto px-4 sm:px-6 lg:px-10 h-14 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <div className="h-7 w-7 rounded-md bg-[color:var(--brand-primary)] text-[color:var(--bg-0)] flex items-center justify-center">
            <Gem className="h-4 w-4" />
          </div>
          <span className="font-display text-lg font-semibold tracking-tight">Postmoro</span>
        </div>
        <button
          type="button"
          onClick={handleCancel}
          data-testid="login-2fa-cancel"
          className="text-sm text-[color:var(--text-3)] hover:text-[color:var(--text-1)] transition"
        >
          Cancel and sign out
        </button>
      </header>

      <main className="relative">
        <div className="max-w-[460px] mx-auto px-4 sm:px-6 py-10 sm:py-14">
          <motion.div
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4 }}
            className="rounded-2xl bg-[color:var(--surface-1)] border border-[color:var(--border-subtle)] shadow-[var(--shadow-card)] p-6 sm:p-7"
          >
            <div className="flex items-center gap-3 mb-4">
              <div className="h-10 w-10 rounded-lg bg-[color:rgba(27,168,159,0.12)] text-[color:var(--brand-primary)] flex items-center justify-center">
                <ShieldCheck className="h-5 w-5" />
              </div>
              <div>
                <h1
                  className="font-display text-xl font-semibold tracking-tight"
                  data-testid="login-2fa-title"
                >
                  Two-factor authentication
                </h1>
                <p className="text-xs text-[color:var(--text-3)] mt-0.5">
                  Signed in as <span className="text-[color:var(--text-2)]">{email || "your account"}</span>
                </p>
              </div>
            </div>

            <div
              className="grid grid-cols-2 gap-1 p-1 rounded-lg bg-[color:var(--surface-2)] border border-[color:var(--border-subtle)] mb-5"
              data-testid="login-2fa-mode-tabs"
            >
              {["code", "recovery"].map((m) => {
                const active = mode === m;
                return (
                  <button
                    key={m}
                    type="button"
                    onClick={() => { setMode(m); setErrorMsg(""); }}
                    data-testid={`login-2fa-mode-${m}`}
                    className={
                      "text-xs font-medium px-3 py-1.5 rounded transition " +
                      (active
                        ? "bg-[color:var(--surface-1)] text-[color:var(--text-1)] shadow-sm"
                        : "text-[color:var(--text-3)] hover:text-[color:var(--text-1)]")
                    }
                  >
                    {m === "code" ? "Authenticator code" : "Recovery code"}
                  </button>
                );
              })}
            </div>

            <form onSubmit={handleSubmit} className="space-y-4" data-testid="login-2fa-form">
              {mode === "code" ? (
                <div>
                  <label className="text-xs text-[color:var(--text-2)]">
                    6-digit code from your authenticator app
                  </label>
                  <Input
                    type="text"
                    inputMode="numeric"
                    autoComplete="one-time-code"
                    pattern="[0-9]*"
                    maxLength={6}
                    placeholder="123456"
                    value={code}
                    onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))}
                    data-testid="login-2fa-code-input"
                    className="mt-1.5 bg-[color:var(--surface-2)] border-[color:var(--border-subtle)] text-[color:var(--text-1)] placeholder:text-[color:var(--text-3)] tracking-[0.4em] text-center font-mono text-lg"
                    autoFocus
                    required
                  />
                  <p className="text-[11px] text-[color:var(--text-3)] mt-2">
                    Open Google Authenticator, 1Password, Authy, or a similar app and enter the current code.
                  </p>
                </div>
              ) : (
                <div>
                  <label className="text-xs text-[color:var(--text-2)]">
                    Recovery code
                  </label>
                  <Input
                    type="text"
                    autoComplete="one-time-code"
                    placeholder="XXXX-XXXX"
                    value={recoveryCode}
                    onChange={(e) => setRecoveryCode(e.target.value.toUpperCase())}
                    data-testid="login-2fa-recovery-input"
                    className="mt-1.5 bg-[color:var(--surface-2)] border-[color:var(--border-subtle)] text-[color:var(--text-1)] placeholder:text-[color:var(--text-3)] font-mono tracking-widest text-center uppercase"
                    autoFocus
                    required
                  />
                  <p className="text-[11px] text-[color:var(--text-3)] mt-2">
                    Enter one of the 10 single-use codes you saved when you enabled 2FA.
                  </p>
                </div>
              )}

              {errorMsg ? (
                <div
                  className="text-xs text-[color:var(--danger)] bg-[color:rgba(238,90,82,0.06)] border border-[color:rgba(238,90,82,0.30)] rounded-md px-3 py-2"
                  data-testid="login-2fa-error"
                  role="alert"
                >
                  {errorMsg}
                </div>
              ) : null}

              <Button
                type="submit"
                disabled={loading}
                data-testid="login-2fa-submit"
                className="w-full bg-[color:var(--brand-primary)] hover:bg-[color:var(--brand-primary-hover)] text-[color:var(--bg-0)] font-semibold"
              >
                {loading ? (
                  <span className="inline-flex items-center">
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Verifying…
                  </span>
                ) : (
                  <>
                    {mode === "code" ? (
                      <ShieldCheck className="h-4 w-4 mr-2" />
                    ) : (
                      <KeyRound className="h-4 w-4 mr-2" />
                    )}
                    Verify and continue
                    <ArrowRight className="h-3.5 w-3.5 ml-1.5" />
                  </>
                )}
              </Button>

              <p className="text-[11px] text-[color:var(--text-3)] leading-relaxed">
                Lost access to your authenticator and your recovery codes?{" "}
                <a
                  href="mailto:support@postmoro.com"
                  className="text-[color:var(--brand-primary)] hover:underline"
                  data-testid="login-2fa-support-link"
                >
                  Contact support
                </a>
                {" "}so we can help you recover your account.
              </p>
            </form>
          </motion.div>
        </div>
      </main>
    </div>
  );
}
